<template>
    <div v-if="hasCurrentListingAndCurrentTitle" class="catalog-listing-detail-container">
        <ModifyTitle
            id="modify-title"
            :key="designatorRenderKey"
            class="box-shadow edit-title"
            v-bind="{ ...currentTitle, designators: localDesignators }"
            @changes="updateTitleModifications"
            @removal="removeTitleModifications"
        />

        <DigitalPrices id="digital-pricing" :productCode="currentTitle.productCode" class="box-shadow" />

        <SlingshotAccessCode
            :hasSlingshotDesignator="hasSlingshotDesignator"
            :duration.sync="slingshotDuration"
            :instructions.sync="slingshotInstructions"
            :durationError="durationError"
            class="slingshot-access-code grid-item"
        />

        <SlingshotLibraries
            :selected-libraries="slingshotLibraries"
            :titleIsbn="currentTitle.productCode"
            class="slingshot-libraries multi-option-container box-shadow"
        />
        <VendorLibraries
            :selected-libraries="vendorLibraries"
            :titleIsbn="currentTitle.productCode"
            class="vendor-libraries multi-option-container box-shadow"
        />

        <CatalogListingDesignators
            :preSelectedList="currentCatalogListingDesignators"
            class="overrides multi-option-container box-shadow"
            @changes="updateCurrentCatalogListing"
        />

        <!-- Listing Notes -->
        <div class="listing-notes listing-detail-info-container box-shadow">
            <div class="listing-detail-info-content">
                <h2>Listing Notes</h2>
                <v-textarea v-model="listingNotes" outlined rows="5" placeholder="Notes for this listing"></v-textarea>
            </div>
        </div>

        <!-- External Listing Notes -->
        <div class="external-listing-notes listing-detail-info-container box-shadow">
            <div class="listing-detail-info-content">
                <h2>External Listing Notes</h2>
                <p>{{ currentCatalogListing.externalListingNote }}</p>
            </div>
        </div>

        <ContextualInfoGroup class="catalog-information" />

        <!-- Inclusive Access Assign Pricing record -->
        <InclusiveAccessPrices
            id="ia-pricing"
            :iaActive="currentCatalogListing.ia"
            :title="currentTitle"
            selectablePrices
            @changes="setCurrentCatalogListingPricingInfo"
            @removal="setCurrentCatalogListingPricingInfo"
        />
    </div>
</template>

<script>
// Vuex
import { mapActions, mapGetters, mapState } from 'vuex';

// Components
import DigitalPrices from '@/views/common/DigitalPrices';
import SlingshotAccessCode from '@/components/multioption/SlingshotAccessCode';
import InclusiveAccessPrices from '@/views/common/InclusiveAccessPrices';
import ModifyTitle from '@/views/common/ModifyTitle';
import CatalogListingDesignators from '@/views/common/CatalogListingDesignators';
import SlingshotLibraries from '@/components/multioption/SlingshotLibraries';
import VendorLibraries from '@/components/multioption/VendorLibraries';
import ContextualInfoGroup from './listingDetail/ContextualInfoGroup';

export default {
    name: 'ListingDetail',
    components: {
        SlingshotAccessCode,
        VendorLibraries,
        SlingshotLibraries,
        ContextualInfoGroup,
        DigitalPrices,
        InclusiveAccessPrices,
        ModifyTitle,
        CatalogListingDesignators,
    },
    data() {
        return {
            slingshotDuration: null,
            slingshotInstructions: null,
            localDesignators: [],
            originalHadSlingshotDesignator: false,
            designatorRenderKey: 0,
            durationError: false,
        };
    },
    computed: {
        hasSlingshotDesignator() {
            return this.localDesignators.some(
                (designator) => designator.name === 'SLINGSHOT_DIGITAL_ACCESS_CODE',
            );
        },
        hasSlingshotDesignatorChanged() {
            return this.originalHadSlingshotDesignator !== this.hasSlingshotDesignator;
        },
        ...mapGetters({
            hasCatalogListings: 'CatalogListing/hasListings',
            hasCurrentTitle: 'hasCurrentTitle',
            currentCatalogListingDesignators: 'currentCatalogListingDesignators',
        }),
        ...mapState({
            currentCatalogListing: (state) => state.currentCatalogListing,
            currentTitle: (state) => state.currentTitle,
            selectedLibraries: (state) => state.Title.LibraryManagement.selectedLibraries,
        }),

        slingshotLibraries() {
            return this.selectedLibraries.filter(({ manuallyManaged }) => manuallyManaged);
        },
        vendorLibraries() {
            return this.selectedLibraries.filter(({ manuallyManaged }) => !manuallyManaged);
        },

        /** @returns { Boolean } */
        hasCurrentListingAndCurrentTitle() {
            return this.hasCatalogListings && this.hasCurrentTitle;
        },
        listingNotes: {
            get() {
                return this.currentCatalogListing.note;
            },
            set(note) {
                this.updateCurrentCatalogListing({
                    key: 'note',
                    value: note,
                });
            },
        },
    },
    watch: {
        currentTitle: {
            immediate: true,
            handler(newValue) {
                if (!newValue) return;

                this.slingshotDuration = newValue.slingshotDigitalAccessCodeDuration;
                this.slingshotInstructions = newValue.slingshotDigitalAccessCodeInstructions;
                this.localDesignators = [...(newValue.designators || [])];
                this.originalHadSlingshotDesignator = this.localDesignators.some(
                    (d) => d.name === 'SLINGSHOT_DIGITAL_ACCESS_CODE',
                );
            },
        },
        slingshotDuration(value) {
            // Force integer and min -1
            if (value !== null && value !== '') {
                let sanitized = Math.floor(Number(value));
                if (sanitized < -1) sanitized = -1;
                this.slingshotDuration = sanitized;
            }

            this.updateTitleModifications({
                key: 'slingshotDigitalAccessCodeDuration',
                value: this.slingshotDuration,
            });

            // Clear error if valid duration is set
            if (this.slingshotDuration !== null && this.slingshotDuration !== '' && this.slingshotDuration !== 0) {
                this.durationError = false;
            }
        },
        slingshotInstructions(value) {
            this.updateTitleModifications({
                key: 'slingshotDigitalAccessCodeInstructions',
                value,
            });
        },
    },
    beforeMount() {
        this.initializeGlobalResources();
        this.fetchSelectedLibraries(this.currentTitle.productCode);
    },
    methods: {
        updateTitleModifications(changes) {
            if (changes.key === 'designators') {
                this.localDesignators = changes.value || [];
                if (!this.hasSlingshotDesignator) {
                    this.durationError = false;
                }
            }

            // Dispatch all changes to store
            this.$store.dispatch('CatalogListing/Detail/updateTitleModifications', changes);
        },

        validateBeforeSave() {
            if (!this.hasSlingshotDesignator) return true;

            const duration = this.slingshotDuration;

            // Check empty / zero
            if (duration === null || duration === '' || duration === 0) {
                this.durationError = true;
                this.$store.dispatch(
                    'setErrorNotification',
                    'Duration is required when Slingshot Access Code designator is selected',
                    { root: true },
                );
                return false;
            }

            this.durationError = false;
            return true;
        },

        confirmSlingshotChange() {
            this.originalHadSlingshotDesignator = this.hasSlingshotDesignator;
        },

        revertDesignatorChange(shouldHaveSlingshot) {
            const originalDesignators = this.currentTitle.designators || [];

            if (shouldHaveSlingshot) {
                this.localDesignators = [...originalDesignators];
            } else {
                this.localDesignators = originalDesignators.filter(
                    (d) => d.name !== 'SLINGSHOT_DIGITAL_ACCESS_CODE',
                );
            }

            this.$store.dispatch('CatalogListing/Detail/updateTitleModifications', {
                key: 'designators',
                value: this.localDesignators,
            });

            this.designatorRenderKey += 1;

            if (!shouldHaveSlingshot) {
                this.durationError = false;
            }
        },

        ...mapActions({
            initializeGlobalResources: 'GlobalResources/init',
            initializeTitleModifications: 'CatalogListing/Detail/initializeTitleModifications',
            removeTitleModifications: 'CatalogListing/Detail/removeTitleModifications',
            updateCurrentCatalogListing: 'updateCurrentCatalogListing',
            fetchSelectedLibraries: 'Title/LibraryManagement/fetchSelectedLibraries',
        }),

        setCurrentCatalogListingPricingInfo(attributes) {
            Object.keys(attributes).forEach((key) => {
                this.updateCurrentCatalogListing({
                    key,
                    value: attributes[key],
                });
            });
        },
    },
};
</script>

<style lang="scss">
@import "@/assets/sass/variables.scss";
@import "@/assets/sass/listingdetails.scss";

/* Do NOT scope this style tag */
/* Several child components rely on these styles */
/* Name styles very specifically, and we won't have a problem */

.listing-detail-info-container {
    color: $list-container-fg;
    text-align: left;
    border-radius: 2px;
    padding: 1rem;
}

.cancel-button {
    grid-column-start: 6;
    cursor: pointer;
}

h2,
h3,
h4,
h5 {
    margin-top: 0;
}

h2 {
    font-variant-caps: all-small-caps;
}

h3 {
    font-weight: normal;
    font-size: 0.8rem;
}

.multi-option-container {
    padding: 1rem;
}

.catalog-listing-detail-container {
    display: grid;
    grid-template:
        "title digital-pricing"
        "title slingshot-access-code"
        "title slingshot-libraries"
        "title vendor-libraries"
        "overrides overrides"
        "listing-notes external"
        "ia-pricing ia-pricing"
        "context context"
        /1fr 1fr;
    grid-row-gap: 20px;
    grid-column-gap: 1%;
    margin-bottom: 8rem;
}

.catalog-listing-detail-container {
    .edit-title {
        grid-area: title;
        padding: 1rem;

        &__designators {
            margin-top: 1rem;
        }

        &__notes {
            margin-top: 1rem;
        }
    }

    .external-listing-notes {
        grid-area: external;
    }

    .listing-notes {
        grid-area: listing-notes;
    }

    .catalog-information {
        grid-area: context;
    }
}

#ia-pricing {
    grid-area: ia-pricing;
}
</style>
